import React, { useMemo } from 'react';
import type { CoopClue, CoopCrosswordBoard } from '../../core/types/payloads/coop.types';
import { buildCrosswordGrid, cellKey, type CrosswordGridMeta } from './crossword.engine';

export interface CrosswordPreviewGridProps {
  board: CoopCrosswordBoard;
  activeClueId?: string;
  showSolution?: boolean;
  size?: 'md' | 'lg' | 'fill';
  onCellClick?: (row: number, col: number, clueIds: string[]) => void;
}

function EditorGrid({
  board,
  meta,
  activeClueId,
  showSolution,
  size,
  onCellClick,
}: {
  board: CoopCrosswordBoard;
  meta: CrosswordGridMeta;
  activeClueId?: string;
  showSolution?: boolean;
  size: 'md' | 'lg' | 'fill';
  onCellClick?: (row: number, col: number, clueIds: string[]) => void;
}) {
  const activeCells = activeClueId ? new Set(meta.clueCells[activeClueId] ?? []) : new Set<string>();
  const cellSize =
    size === 'fill' ? 'min(38px, calc(72vw / var(--cw-cols, 6)))' : size === 'lg' ? 'min(42px, 7vw)' : 'min(34px, 9vw)';

  return (
    <div
      className="crossword-preview-wrap"
      style={{ '--cw-cols': board.cols, '--cw-rows': board.rows } as React.CSSProperties}
    >
      <div
        className="crossword-grid crossword-grid--editor inline-grid"
        style={{ gridTemplateColumns: `repeat(${board.cols}, ${cellSize})` }}
      >
        {Array.from({ length: board.rows }, (_, row) =>
          Array.from({ length: board.cols }, (_, col) => {
            const key = cellKey(row, col);
            const blocked = meta.blocked.has(key);
            const inWord = activeCells.has(key);
            const number = meta.cellNumbers[key];
            const letter = showSolution ? meta.solution[key] : '';
            const clueIds = meta.cluesByCell[key] ?? [];

            if (blocked) {
              return <div key={key} className="crossword-cell crossword-cell--void aspect-square" aria-hidden />;
            }

            return (
              <button
                key={key}
                type="button"
                className={`crossword-cell crossword-cell--live relative flex aspect-square items-center justify-center ${
                  inWord ? 'crossword-cell--active' : ''
                }`}
                onClick={() => onCellClick?.(row, col, clueIds)}
                title={clueIds.length ? `Pista ${meta.clueNumbers[clueIds[0]!]}` : undefined}
              >
                {number != null && <span className="crossword-cell__number">{number}</span>}
                <span className="crossword-cell__letter">{letter}</span>
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}

export function CrosswordPreviewGrid({
  board,
  activeClueId,
  showSolution = true,
  size = 'fill',
  onCellClick,
}: CrosswordPreviewGridProps) {
  const meta = useMemo(() => {
    try {
      return buildCrosswordGrid(board);
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Cuadrícula inválida' };
    }
  }, [board]);

  if ('error' in meta) {
    return (
      <div className="crossword-preview-error">
        <p className="font-display text-sm font-semibold tracking-tight">Revisa las pistas</p>
        <p className="mt-1 text-xs text-slate-400">{meta.error}</p>
      </div>
    );
  }

  return (
    <EditorGrid
      board={board}
      meta={meta}
      activeClueId={activeClueId}
      showSolution={showSolution}
      size={size}
      onCellClick={onCellClick}
    />
  );
}

export function findClueAtCell(board: CoopCrosswordBoard, row: number, col: number): CoopClue | undefined {
  return board.clues.find((c) => c.row === row && c.col === col);
}
